import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { restaurantInfo, menuHighlights, beverages, familyBundles, catering, menuGlossary } from "../data/mock";
import { Printer } from "lucide-react";

/**
 * Print-ready physical menu — one double-sided A4 sheet.
 *   Front: brand header + every food dish in two balanced columns (no
 *          section ever splits across a column or page).
 *   Back:  drinks, family bundles, catering strip, find-us / hours / QR.
 * Each .sheet is exactly 210×297mm with 12mm margins so the browser's
 * Print → Save as PDF output is two clean pages.
 */

const SITE = "mamaminassoulfeed.online";
const MENU_QR_URL = `https://${SITE}/#menu`;
const FALLBACK_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_soul-food-table/artifacts/mkkcow2t_IMG_1755.webp";
const ROW_MM = 18;   // estimated dish row height incl. gap
const HEAD_MM = 11;  // estimated section heading incl. spacing

const priceText = (p) => (p || "").replace("$", "A$");

function groupByTag(dishes) {
  const groups = [];
  const byTag = {};
  dishes.forEach((d) => {
    const tag = d.tag || "Menu";
    if (!byTag[tag]) {
      byTag[tag] = { tag, items: [] };
      groups.push(byTag[tag]);
    }
    byTag[tag].items.push(d);
  });
  return groups;
}

// Two-column partition with the smallest height difference; ties favour
// natural reading order. The first section always leads the left column.
function splitColumns(groups) {
  const h = (g) => HEAD_MM + g.items.length * ROW_MM;
  const n = groups.length;
  let best = [groups, []];
  let bestKey = null;
  for (let mask = 1; mask < 2 ** n; mask += 2) {
    const inLeft = (i) => (mask >> i) & 1;
    const left = groups.filter((_, i) => inLeft(i));
    const right = groups.filter((_, i) => !inLeft(i));
    const diff = Math.abs(left.reduce((s, g) => s + h(g), 0) - right.reduce((s, g) => s + h(g), 0));
    let inversions = 0;
    for (let i = 0; i < n; i += 1) for (let j = i + 1; j < n; j += 1) if (!inLeft(i) && inLeft(j)) inversions += 1;
    const key = Math.round(diff) * 100 + inversions;
    if (bestKey === null || key < bestKey) { bestKey = key; best = [left, right]; }
  }
  return best;
}

function stripGroupPrefix(name, title) {
  const first = title.split(" ")[0];
  return name.startsWith(`${first} `) ? name.slice(first.length + 1) : name;
}

function Photo({ src, alt, className }) {
  return (
    <div className={className}>
      <img src={src || FALLBACK_IMG} alt={alt} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} />
    </div>
  );
}

function BrandHeader({ kicker }) {
  return (
    <header className="brand">
      <img src="/media/logo-600.png" alt="Mamamina's Soulfeed" className="brand-logo" />
      <div className="brand-text">
        <div className="brand-name">Mamamina's <em>Soulfeed</em></div>
        <div className="brand-kicker">{kicker}</div>
      </div>
      <div className="brand-right">
        <div className="brand-right-label">Order online</div>
        <div className="brand-right-url">{SITE}</div>
      </div>
    </header>
  );
}

function SectionHeading({ title }) {
  return (
    <div className="section-heading">
      <span className="section-title">{title}</span>
      <span className="section-rule" />
    </div>
  );
}

function DishRow({ item, testId }) {
  return (
    <div className="dish" data-testid={testId}>
      <Photo src={item.image} alt={item.name} className="dish-photo" />
      <div className="dish-body">
        <div className="dish-head">
          <h3 className="dish-name">{item.name}</h3>
          <span className="dish-price">{priceText(item.price)}</span>
        </div>
        <p className="dish-desc">{item.description}</p>
      </div>
    </div>
  );
}

function FoodSheet({ columns }) {
  return (
    <div className="sheet sheet-food" data-testid="print-menu-sheet-food">
      <BrandHeader kicker="Acacia Ridge · Brisbane · Liberian kitchen · حلال Halal" />
      <div className="columns">
        {columns.map((col, i) => (
          <div className="column" key={i} data-testid={`print-menu-column-${i + 1}`}>
            {col.map((g) => (
              <section key={g.tag} className="section" data-testid={`print-menu-section-${g.tag.toLowerCase().replace(/\s+/g, "-")}`}>
                <SectionHeading title={g.tag} />
                {g.items.map((item) => <DishRow key={item.id} item={item} testId={`menu-item-${item.id}`} />)}
              </section>
            ))}
          </div>
        ))}
      </div>
      <footer className="sheet-foot">
        <span>Drinks · family bundles · hours & how to find us — over the page</span>
        <span>1 / 2</span>
      </footer>
    </div>
  );
}

function FeaturedCard({ item }) {
  return (
    <div className="featured" data-testid={`print-menu-featured-${item.lookup_key}`}>
      <Photo src={item.image} alt={item.name} className="featured-photo" />
      <div className="dish-head">
        <h3 className="dish-name">{item.name}</h3>
        <span className="dish-price">{priceText(item.price)}</span>
      </div>
      <p className="dish-desc">{item.description}</p>
    </div>
  );
}

function DrinkGroup({ group }) {
  return (
    <div className="drink-group" data-testid={`print-menu-drinks-${group.title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="drink-group-title">{group.title}</div>
      {group.items.map((it) => (
        <div className="drink-row" key={it.name}>
          <span>{stripGroupPrefix(it.name, group.title)}</span>
          <span>{priceText(it.price)}</span>
        </div>
      ))}
    </div>
  );
}

function BundleCard({ bundle }) {
  return (
    <div className="bundle" data-testid={`print-menu-bundle-${bundle.id}`}>
      <Photo src={bundle.image} alt={bundle.name} className="bundle-photo" />
      <div className="dish-body">
        <div className="bundle-name">{bundle.name}</div>
        <div className="bundle-tag">{bundle.tagline}</div>
        <div className="bundle-price">{priceText(bundle.price)}<s>{priceText(bundle.originalPrice)}</s></div>
        <p className="bundle-desc">{bundle.description}</p>
      </div>
    </div>
  );
}

function Glossary() {
  const half = Math.ceil(menuGlossary.length / 2);
  const cols = [menuGlossary.slice(0, half), menuGlossary.slice(half)];
  return (
    <section data-testid="print-menu-glossary">
      <SectionHeading title="Good to know — a Liberian kitchen glossary" />
      <div className="glossary">
        {cols.map((col, i) => (
          <dl key={i} className="glossary-col">
            {col.map((g) => (
              <div className="glossary-row" key={g.term}>
                <dt>{g.term}</dt>
                <dd>{g.meaning}</dd>
              </div>
            ))}
          </dl>
        ))}
      </div>
    </section>
  );
}

function FindUs() {
  const { address, contact, hours, delivery } = restaurantInfo;
  return (
    <footer className="find-us" data-testid="print-menu-find-us">
      <div>
        <div className="kicker">Find us / Order</div>
        <div className="find-line">{address.line1}</div>
        <div className="find-line">{address.line2}</div>
        <div className="find-phone">{contact.mobileDisplay}</div>
        <div className="find-muted">or {contact.phoneDisplay}</div>
        <div className="find-muted">{contact.email}</div>
        <div className="find-muted">Also on {delivery.map((d) => d.name).join(" · ")}</div>
        <div className="find-catering" data-testid="print-menu-catering">
          <strong>Catering & events</strong> — {catering.services.map((s) => s.title.toLowerCase()).join(", ")}. Call {contact.mobileDisplay}.
        </div>
      </div>
      <div>
        <div className="kicker">Open hours</div>
        {hours.map((h) => (
          <div className="hours-row" key={h.label}><span>{h.label}</span><span>{h.time}</span></div>
        ))}
      </div>
      <div className="find-qr">
        <QRCodeSVG value={MENU_QR_URL} size={95} bgColor="#f6ebd0" fgColor="#1a1512" level="M" />
        <div className="qr-caption">
          <strong>Scan to order</strong><br />{SITE}<br />10% off your first order — code <strong>WELCOME10</strong>
        </div>
      </div>
    </footer>
  );
}

function BackSheet() {
  return (
    <div className="sheet sheet-back" data-testid="print-menu-sheet-back">
      <BrandHeader kicker="Drinks · Family bundles · Good to know · Find us" />
      <div className="back-body">
        <section>
          <SectionHeading title="Drinks" />
          <div className="featured-row">
            {beverages.featured.map((d) => <FeaturedCard key={d.name} item={d} />)}
          </div>
          <div className="drink-groups">
            {beverages.groups.map((g) => <DrinkGroup key={g.title} group={g} />)}
          </div>
        </section>
        <section>
          <SectionHeading title="Family bundles" />
          <div className="bundle-row">
            {familyBundles.map((b) => <BundleCard key={b.id} bundle={b} />)}
          </div>
        </section>
        <Glossary />
        <FindUs />
      </div>
      <footer className="sheet-foot">
        <span>{SITE} · 100% Halal · Liberian & West African kitchen</span>
        <span>2 / 2</span>
      </footer>
    </div>
  );
}

export default function PrintMenu() {
  const columns = splitColumns(groupByTag(menuHighlights));

  return (
    <div className="print-menu-shell">
      <MenuStyles />

      <header className="print-header no-print">
        <div>
          <h1>Physical menu · Double-sided A4</h1>
          <p>Front: full food menu · Back: drinks, family bundles, Liberian glossary, hours & QR. Prints as exactly two pages.</p>
        </div>
        <button className="print-btn" onClick={() => window.print()} data-testid="print-menu-btn">
          <Printer size={16} /> Print / Save PDF
        </button>
      </header>

      <div className="sheets">
        <FoodSheet columns={columns} />
        <BackSheet />
      </div>

      <footer className="no-print print-footer">
        <p>Printer dialog: <strong>A4</strong>, <strong>100% scale</strong>, <strong>margins: none</strong>, <strong>colour</strong>, <strong>background graphics ON</strong>, <strong>two-sided (flip on long edge)</strong>. Suggested stock: 240gsm cream or uncoated, laminated.</p>
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function MenuStyles() {
  return (
    <style>{`
      .print-menu-shell {
        min-height: 100vh; background: #0f0b09; color: #e4dcc9;
        padding: 32px 16px 64px; font-family: -apple-system, "Segoe UI", sans-serif;
      }
      .print-header {
        max-width: 900px; margin: 0 auto 24px; display: flex;
        align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
      }
      .print-header h1 { font-size: 22px; margin: 0 0 4px; color: #f6ebd0; }
      .print-header p { font-size: 12px; color: #a09484; margin: 0; }
      .print-btn {
        display: inline-flex; align-items: center; gap: 8px;
        background: #B7853D; color: #1a1512; border: none;
        padding: 10px 18px; font-weight: 600; cursor: pointer; border-radius: 4px; font-size: 13px;
      }
      .print-footer { max-width: 900px; margin: 24px auto 0; color: #a09484; font-size: 12px; }

      .sheets { display: flex; flex-direction: column; align-items: center; gap: 10mm; }
      .sheet {
        width: 210mm; height: 297mm; box-sizing: border-box; padding: 12mm;
        background: #f6ebd0; color: #1a1512; display: flex; flex-direction: column;
        overflow: hidden; box-shadow: 0 4px 24px rgba(0,0,0,.4);
      }

      /* --- Brand header --- */
      .brand {
        display: flex; align-items: center; gap: 5mm; padding-bottom: 3.5mm;
        border-bottom: 0.3mm solid #B7853D; margin-bottom: 4mm;
      }
      .brand-logo { width: 19mm; height: 19mm; object-fit: contain; flex-shrink: 0; }
      .brand-text { flex: 1; min-width: 0; }
      .brand-name { font-family: Georgia, serif; font-style: italic; font-size: 8mm; line-height: 1; color: #1a1512; }
      .brand-name em { color: #B7853D; }
      .brand-kicker {
        margin-top: 1.8mm; font-size: 2.3mm; letter-spacing: 0.7mm;
        text-transform: uppercase; color: #5a4d40;
      }
      .brand-right { text-align: right; }
      .brand-right-label { font-size: 2.2mm; letter-spacing: 0.6mm; text-transform: uppercase; color: #a09484; }
      .brand-right-url {
        font-family: Georgia, serif; font-style: italic; font-size: 3.6mm; color: #B7853D; margin-top: 0.8mm;
      }

      /* --- Food columns --- */
      .columns { display: grid; grid-template-columns: 1fr 1fr; gap: 0 6mm; flex: 1; min-height: 0; }
      .column { min-width: 0; overflow: hidden; }
      .section { margin-bottom: 4.5mm; break-inside: avoid; }
      .section-heading { display: flex; align-items: center; gap: 3mm; margin-bottom: 2.2mm; }
      .section-title {
        font-family: Georgia, serif; font-style: italic; font-size: 4.6mm; line-height: 1; color: #1a1512;
      }
      .section-rule { flex: 1; height: 0.25mm; background: #B7853D; opacity: .55; }

      .dish { display: flex; gap: 3mm; align-items: flex-start; margin-bottom: 3mm; break-inside: avoid; }
      .dish:last-child { margin-bottom: 0; }
      .dish-photo, .bundle-photo {
        flex-shrink: 0; border-radius: 1.2mm; overflow: hidden;
        border: 0.2mm solid #d4c9b0; background: #e4dcc9;
      }
      .dish-photo { width: 15mm; height: 15mm; }
      .dish-photo img, .bundle-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
      .dish-body { flex: 1; min-width: 0; }
      .dish-head { display: flex; justify-content: space-between; align-items: baseline; gap: 2.5mm; }
      .dish-name {
        font-family: Georgia, serif; font-size: 3.5mm; line-height: 1.15;
        font-weight: 600; margin: 0; color: #1a1512;
      }
      .dish-price {
        font-family: Georgia, serif; font-size: 3.5mm; font-weight: 600; color: #B7853D; white-space: nowrap;
      }
      .dish-desc {
        margin: 0.8mm 0 0; font-size: 2.5mm; line-height: 1.3; color: #4a3d30;
        display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden;
      }

      .sheet-foot {
        margin-top: auto; padding-top: 2.5mm; border-top: 0.25mm solid rgba(183,133,61,.5);
        display: flex; justify-content: space-between; gap: 4mm;
        font-size: 2.3mm; letter-spacing: 0.4mm; text-transform: uppercase; color: #a09484;
      }

      /* --- Back sheet --- */
      .back-body { flex: 1; min-height: 0; display: flex; flex-direction: column; justify-content: space-between; gap: 4mm; margin-bottom: 4mm; }
      .featured-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4mm; margin-bottom: 5mm; }
      .featured-photo {
        width: 100%; aspect-ratio: 58 / 28; overflow: hidden; margin-bottom: 2.2mm;
        border: 0.2mm solid #d4c9b0; background: #e4dcc9;
      }
      .featured-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
      .featured .dish-desc { -webkit-line-clamp: 3; }
      .drink-groups { display: grid; grid-template-columns: repeat(5, 1fr); gap: 3mm 4mm; }
      .drink-group { min-width: 0; }
      .drink-group-title {
        font-family: Georgia, serif; font-style: italic; font-size: 3.4mm; color: #B7853D;
        margin-bottom: 1.5mm; padding-bottom: 0.8mm; border-bottom: 0.2mm solid rgba(183,133,61,.4);
      }
      .drink-row { display: flex; justify-content: space-between; gap: 2mm; font-size: 2.5mm; line-height: 1.55; color: #1a1512; }
      .drink-row span:last-child { color: #5a4d40; }

      .bundle-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4mm; }
      .bundle { display: flex; gap: 3mm; align-items: flex-start; }
      .bundle-photo { width: 20mm; height: 20mm; }
      .bundle-name { font-family: Georgia, serif; font-size: 3.3mm; font-weight: 600; line-height: 1.15; }
      .bundle-tag { font-size: 2.2mm; letter-spacing: 0.4mm; text-transform: uppercase; color: #a09484; margin-top: 0.5mm; }
      .bundle-price { font-family: Georgia, serif; font-size: 3.4mm; color: #B7853D; font-weight: 600; margin-top: 0.8mm; }
      .bundle-price s { color: #a09484; font-size: 2.4mm; margin-left: 1.5mm; font-weight: 400; }
      .bundle-desc { font-size: 2.3mm; line-height: 1.3; color: #4a3d30; margin: 0.8mm 0 0; }

      .glossary { display: grid; grid-template-columns: 1fr 1fr; gap: 0 6mm; }
      .glossary-col { margin: 0; }
      .glossary-row { display: grid; grid-template-columns: 24mm 1fr; gap: 0 2mm; margin-bottom: 1.4mm; }
      .glossary-row dt { font-family: Georgia, serif; font-weight: 600; font-size: 2.7mm; line-height: 1.35; color: #1a1512; }
      .glossary-row dd { margin: 0; font-size: 2.4mm; line-height: 1.35; color: #4a3d30; }
      .find-catering { margin-top: 1.6mm; font-size: 2.5mm; line-height: 1.4; color: #5a4d40; }
      .find-catering strong { color: #1a1512; font-weight: 600; }

      .find-us {
        border-top: 0.3mm solid #B7853D; padding-top: 4mm;
        display: grid; grid-template-columns: 1.15fr 1fr auto; gap: 6mm; align-items: start;
      }
      .kicker { font-size: 2.3mm; letter-spacing: 0.7mm; text-transform: uppercase; color: #a09484; margin-bottom: 1.5mm; }
      .find-line { font-size: 2.8mm; line-height: 1.4; color: #1a1512; }
      .find-muted { font-size: 2.6mm; line-height: 1.4; color: #5a4d40; }
      .find-phone { font-family: Georgia, serif; font-size: 5mm; color: #B7853D; margin: 1.2mm 0 0.3mm; }
      .hours-row { display: flex; justify-content: space-between; gap: 3mm; font-size: 2.6mm; line-height: 1.5; color: #1a1512; }
      .hours-row span:last-child { color: #5a4d40; }
      .find-qr { display: flex; flex-direction: column; align-items: center; gap: 1.8mm; text-align: center; }
      .qr-caption { font-size: 2.3mm; letter-spacing: 0.3mm; color: #5a4d40; line-height: 1.35; }
      .qr-caption strong { color: #1a1512; }

      @page { size: A4 portrait; margin: 0; }
      @media print {
        body, html { background: white !important; margin: 0; }
        .print-menu-shell { background: white; padding: 0; }
        .no-print { display: none !important; }
        .sheets { display: block; }
        .sheet { box-shadow: none; margin: 0 auto; break-after: page; page-break-after: always; }
        .sheet:last-child { break-after: auto; page-break-after: auto; }
        .sheet, .sheet img, .dish-photo, .bundle-photo, .featured-photo {
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
      }
    `}</style>
  );
}
